import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import {
  BehaviorSubject,
  firstValueFrom,
  map,
  Observable,
  tap,
  withLatestFrom,
} from "rxjs";
import { Report } from "../models/report.model";

@Injectable({ providedIn: "root" })
export class ReportApiService {
  _reports = new BehaviorSubject<Report[]>([]);
  _currentReport = new BehaviorSubject<Report | null>(null);

  reports$: Observable<Report[]>;
  currentReport$: Observable<Report>;

  constructor(private http: HttpClient) {
    this.reports$ = this._reports.asObservable();
    this.currentReport$ = this._currentReport.asObservable();
  }

  upsert(report: Report): void {
    if (report.id) {
      this.http
        .post<Report>(
          `${environment.aristoteApi()}/report/${report.id}`,
          report,
        )
        .subscribe();
    } else {
      this.http
        .post<Report>(`${environment.aristoteApi()}/report/`, report)
        .subscribe();
    }
  }

  async refresh(report: Report) {
    return await firstValueFrom(
      this.http.post<Report>(
        `${environment.aristoteApi()}/report/refresh`,
        report,
      ),
    );
  }

  loadById(id: string): void {
    this.http
      .get<Report>(`${environment.aristoteApi()}/report/${id}`)
      .pipe(
        tap((report) => {
          const states = [...report.states].sort(
            (a, b) => a && b && a.date.getTime() - b.date.getTime(),
          );
          this.setCurrentReport({
            ...report,
            states,
            currentState: report.states?.length
              ? report.states[report.states.length - 1]
              : null,
          });
        }),
        withLatestFrom(this.reports$),
        map(([report, reports]) => {
          const array = [...reports];
          const i = array.findIndex((r) => r.id === report.id);
          if (i > -1) {
            array[i] = report;
          } else {
            array.push(report);
          }
          return array;
        }),
      )
      .subscribe((reports) => {
        this._reports.next(reports);
      });
  }

  findAll() {
    this.http
      .get<Report[]>(`${environment.aristoteApi()}/report/`)
      .subscribe((devices) => this._reports.next(devices));
  }

  setCurrentReport(report: Report | null) {
    this._currentReport.next(report);
  }

  get empty(): Report {
    return {
      states: [],
      interval: 3600000,
      ofId: "",
      ligneId: "",
      articleId: "",
      posteId: "",
      downtimes: [],
    } as Report;
  }
}
